import React, { useState } from "react";
import { Dialog, DialogTitle, DialogContent, Button, TextField, Box, Typography, Grid, MenuItem, Avatar, IconButton, FormHelperText, DialogContentText, DialogActions, CircularProgress } from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import { useParams } from "react-router-dom";
import moment from "moment/moment";
import { useDispatch } from "store";
import { openSnackbar, closeSnackbar } from "store/slices/snackbar";
import Select from "react-select";
import { useUpdateBankGuaranteeMutation } from "store/slices/apis/financialBGApiSlice";
import CalenderSVG from "assets/logo/Calendar";
import { RxCross2 } from "react-icons/rx";

const rightLabelStyle = {
    fontWeight: 500,
    fontSize: "16px",
    lineHeight: "100%",
    color: "#5F5F5F",
    fontFamily: "Outfit, sans-serif",
};

const rightTextStyle = {
    "& .MuiInputBase-input": {
        fontWeight: 400,
        fontSize: "16px",
        lineHeight: "100%",
        letterSpacing: "0.16px",
        color: "#0A0A0C",
        fontFamily: "Outfit, sans-serif",
    },
};

const labelTextStyle = {
    fontWeight: 500,
    fontSize: "16px",
    lineHeight: "100%",
    color: "#5F5F5F",
    fontFamily: "Outfit, sans-serif",
    mb: 1,
    mt: 1,
};

const EditRBG = (props) => {
    const { open, handleClose, rowData, tabKey, statusOptions, issuingBankOptions } = props;
    const { projectId } = useParams();
    const dispatch = useDispatch();

    const [updateBankGuarantee, { isLoading }] = useUpdateBankGuaranteeMutation();

    const [formData, setFormData] = useState({

        contractProjectName: rowData?.contractProjectName || "",
        beneficiary: rowData?.beneficiary || "",
        contractValue: rowData?.contractValue || "",
        issuingBank: rowData?.issuingBank || "",
        branchOrIFSC: rowData?.branchOrIFSC || "",
        bgReferenceNumber: rowData?.bgReferenceNumber || "",
        bgAmount: rowData?.bgAmount || "",
        rbgPercentageOfContract: rowData?.rbgPercentageOfContract || "",
        dateOfIssue: rowData?.dateOfIssue
            ? moment.utc(rowData.dateOfIssue).format("YYYY-MM-DD")
            : null,
        dateOfExpiry: rowData?.dateOfExpiry
            ? moment.utc(rowData.dateOfExpiry).format("YYYY-MM-DD")
            : null,
        status: rowData?.status || "",
        dateOfRelease: rowData?.dateOfRelease
            ? moment.utc(rowData.dateOfRelease).format("YYYY-MM-DD")
            : null,
        daysToExpiry: rowData?.daysToExpiry || "",
        expiryAlert: rowData?.expiryAlert || "",
        validityDays: rowData?.validityDays || "",
        renewalDate: rowData?.renewalDate
            ? moment.utc(rowData.renewalDate).format("YYYY-MM-DD")
            : null,
        claimPeriodDays: rowData?.claimPeriodDays || "",
        remarksOrConditions: rowData?.remarksOrConditions || "",
        lastUpdated: rowData?.lastUpdated
            ? moment.utc(rowData.lastUpdated).format("YYYY-MM-DD")
            : null,
        projectId: projectId,
    });

    const numericField = (fieldName, isRequired = true) => {
        let schema = Yup.string()
            .nullable()
            .test(
                "is-valid-number",
                `${fieldName} must contain only numbers and decimals (no commas or spaces)`,
                (value) => {
                    if (!value) return !isRequired;
                    return /^\d+(\.\d+)?$/.test(value);
                }
            );

        if (isRequired) {
            schema = schema.required(`${fieldName} field is required`);
        }

        return schema;
    };

    const validationSchema = Yup.object({
        contractProjectName: Yup.string().required("Contract / Project Name field is required"),
        beneficiary: Yup.string().required("Beneficiary (Client / Owner) field is required"),
        contractValue: numericField("Contract Value"),
        issuingBank: Yup.string().required("Issuing Bank field is required"),
        branchOrIFSC: Yup.string().required("Branch / IFSC field is required"),
        bgReferenceNumber: Yup.string().required("BG Reference Number field is required"),
        bgAmount: numericField("BG Amount"),
        rbgPercentageOfContract: numericField("RBG % Of Contract"),
        dateOfIssue: Yup.date().required("Date Of Issue field is required"),
        dateOfExpiry: Yup.date().required("Date Of Expiry field is required"),
        status: Yup.string().required("Status field is required"),
        daysToExpiry: numericField("Days To Expiry"),
        expiryAlert: Yup.string().required("Expiry Alert field is required"),

        validityDays: numericField("Validity (Days)", false),
        claimPeriodDays: numericField("Claim Period (Days)", false),
    });

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {

        let reqBody = {
            bankGuaranteeTrackerId: rowData?.bankGuaranteeTrackerId,
            projectId: projectId,
            contractProjectName: values?.contractProjectName,
            beneficiary: values?.beneficiary,
            contractValue: values?.contractValue ? values?.contractValue : 0,
            issuingBank: values?.issuingBank,
            branchOrIFSC: values?.branchOrIFSC,
            bgReferenceNumber: values?.bgReferenceNumber,
            bgAmount: values?.bgAmount ? values?.bgAmount : 0,
            abgPercentageOfContract: 0,
            pbgPercentageOfContract: 0,
            rbgPercentageOfContract: values?.rbgPercentageOfContract ? values?.rbgPercentageOfContract : 0,
            dateOfIssue: values?.dateOfIssue
                ? moment.utc(values?.dateOfIssue, 'YYYY-MM-DD').format('YYYY-MM-DDTHH:mm:ss[Z]')
                : null,
            dateOfExpiry: values?.dateOfExpiry
                ? moment.utc(values?.dateOfExpiry, 'YYYY-MM-DD').format('YYYY-MM-DDTHH:mm:ss[Z]')
                : null,
            status: values?.status,
            dateOfRelease: values?.dateOfRelease
                ? moment.utc(values?.dateOfRelease, 'YYYY-MM-DD').format('YYYY-MM-DDTHH:mm:ss[Z]')
                : null,
            daysToExpiry: values?.daysToExpiry ? values?.daysToExpiry : 0,
            expiryAlert: values?.expiryAlert,
            validityDays: values?.validityDays ? values?.validityDays : 0,
            renewalDate: values?.renewalDate
                ? moment.utc(values?.renewalDate, 'YYYY-MM-DD').format('YYYY-MM-DDTHH:mm:ss[Z]')
                : null,
            claimPeriodDays: values?.claimPeriodDays ? values?.claimPeriodDays : 0,
            remarksOrConditions: values?.remarksOrConditions,
            lastUpdated: values?.lastUpdated
                ? moment.utc(values?.lastUpdated, 'YYYY-MM-DD').format('YYYY-MM-DDTHH:mm:ss[Z]')
                : null,
        };

        try {
            const response = await updateBankGuarantee(reqBody).unwrap();
            openNotification("Bank Guarantee updated successfully", "success");
            resetForm();
            setSubmitting(false);
            handleClose();
        } catch (error) {
            // openNotification(error?.error ?? "Something went wrong", "error");
            //console.log("API ERROR:", error);

            let errorMessage = "Something went wrong";

            if (error?.data?.errors) {
                errorMessage = Object.values(error.data.errors)
                    .flat()
                    .join(", ");
            } else if (error?.data?.title) {
                errorMessage = error.data.title;
            } else if (error?.error) {
                errorMessage = error.error;
            }
            openNotification(errorMessage, "error");
        } finally {
            setSubmitting(false);
        }
    };

    const openNotification = (msg, type = "error", duration = 3000) => {
        dispatch(
            openSnackbar({
                open: true,
                message: msg,
                variant: 'alert',
                alert: {
                    color: type
                },
                close: false
            })
        );
        setTimeout(() => dispatch(closeSnackbar()), duration);
    };

    return (
        <>
            <Dialog open={open}
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: { borderTopLeftRadius: "5px", borderTopRightRadius: "5px", backgroundColor: "#FFFFFF", width: "100%", overflowX: "hidden" },
                }}
                disableEscapeKeyDown
                onClose={(event, reason) => {
                    if (reason !== "backdropClick") {
                        handleClose();
                    }
                }}
            >
                <DialogTitle sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: "1px solid #ddd",
                    backgroundColor: "#ECECEC",
                    pb: 2,
                    pt: 3,
                    width: "100%",
                }}>
                    <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: "27px", lineHeight: "100%", color: "#140F36", fontFamily: "Outfit, sans-serif" }}>
                            Edit RBG
                        </Typography>
                    </Box>
                    <IconButton
                        aria-label="close"
                        onClick={() => handleClose()}
                        sx={(theme) => ({
                            position: "absolute",
                            right: 8,
                            top: 8,
                            color: theme.palette.grey[500],
                        })}
                    >
                        <RxCross2 style={{ fontSize: "30px", color: "#140F36" }} />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Formik
                        initialValues={formData}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                    >
                        {({ values,
                            errors,
                            touched,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                            isSubmitting,
                            setFieldValue,
                            setFieldTouched,
                            setTouched, }) => (
                            <form onSubmit={handleSubmit}>
                                <Grid container spacing={2}>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Contract / Project Name</Typography>
                                        <TextField
                                            fullWidth
                                            name="contractProjectName"
                                            value={values.contractProjectName}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter Contract / Project Name"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.contractProjectName && errors.contractProjectName && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.contractProjectName}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Beneficiary (Client / Owner)</Typography>
                                        <TextField
                                            fullWidth
                                            name="beneficiary"
                                            value={values.beneficiary}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter Beneficiary (Client / Owner)"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.beneficiary && errors.beneficiary && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.beneficiary}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Contract Value (₹ L)</Typography>
                                        <TextField
                                            fullWidth
                                            name="contractValue"
                                            value={values.contractValue}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter Contract Value (₹ L)"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.contractValue && errors.contractValue && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.contractValue}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Issuing Bank</Typography>
                                        <Select
                                            name="issuingBank"
                                            options={issuingBankOptions}
                                            value={issuingBankOptions.find(opt => opt.value === String(values.issuingBank)) || null}
                                            onChange={(option) => setFieldValue("issuingBank", option.value)}
                                            onBlur={() => setFieldTouched("issuingBank", true)}
                                            placeholder="Select Issuing Bank"
                                        />
                                        {touched.issuingBank && errors.issuingBank && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.issuingBank}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Branch / IFSC</Typography>
                                        <TextField
                                            fullWidth
                                            name="branchOrIFSC"
                                            value={values.branchOrIFSC}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter Expectation Branch / IFSC"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.branchOrIFSC && errors.branchOrIFSC && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.branchOrIFSC}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>BG Reference Number</Typography>
                                        <TextField
                                            fullWidth
                                            name="bgReferenceNumber"
                                            value={values.bgReferenceNumber}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter BG Reference Number"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.bgReferenceNumber && errors.bgReferenceNumber && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.bgReferenceNumber}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>BG Amount (₹ L)</Typography>
                                        <TextField
                                            fullWidth
                                            name="bgAmount"
                                            value={values.bgAmount}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter BG Amount (₹ L)"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.bgAmount && errors.bgAmount && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.bgAmount}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>RBG % Of Contract</Typography>
                                        <TextField
                                            fullWidth
                                            name="rbgPercentageOfContract"
                                            value={values.rbgPercentageOfContract}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter RBG % Of Contract"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.rbgPercentageOfContract && errors.rbgPercentageOfContract && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.rbgPercentageOfContract}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={{ ...rightLabelStyle, mb: 1, mt: 1 }}>
                                            Date Of Issue
                                        </Typography>
                                        <TextField
                                            type="date"
                                            name="dateOfIssue"
                                            value={values.dateOfIssue}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            onClick={(e) => {
                                                const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                if (input) input.showPicker?.();
                                            }}
                                            fullWidth
                                            sx={{
                                                ...rightTextStyle,
                                                "& input::-webkit-calendar-picker-indicator": {
                                                    display: "none",
                                                    WebkitAppearance: "none",
                                                },
                                                "& input[type='date']": {
                                                    appearance: "none",
                                                    WebkitAppearance: "none",
                                                },
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": {
                                                        border: "none",
                                                    },
                                                },
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}>
                                                        {CalenderSVG}
                                                    </Box>
                                                ),
                                                sx: {
                                                    backgroundColor: "#fff",
                                                    border: '1px solid',
                                                    borderColor: 'hsl(0, 0%, 80%)',
                                                    height: "40px",
                                                    width: { xs: "100%", md: "100%" },
                                                    borderRadius: "5px",
                                                },
                                            }}
                                        />
                                        {touched.dateOfIssue && errors.dateOfIssue && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.dateOfIssue}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={{ ...rightLabelStyle, mb: 1, mt: 1 }}>
                                            Date Of Expiry
                                        </Typography>
                                        <TextField
                                            type="date"
                                            name="dateOfExpiry"
                                            value={values.dateOfExpiry}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            onClick={(e) => {
                                                const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                if (input) input.showPicker?.();
                                            }}
                                            fullWidth
                                            sx={{
                                                ...rightTextStyle,
                                                "& input::-webkit-calendar-picker-indicator": {
                                                    display: "none",
                                                    WebkitAppearance: "none",
                                                },
                                                "& input[type='date']": {
                                                    appearance: "none",
                                                    WebkitAppearance: "none",
                                                },
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": {
                                                        border: "none",
                                                    },
                                                },
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}>
                                                        {CalenderSVG}
                                                    </Box>
                                                ),
                                                sx: {
                                                    backgroundColor: "#fff",
                                                    border: '1px solid',
                                                    borderColor: 'hsl(0, 0%, 80%)',
                                                    height: "40px",
                                                    width: { xs: "100%", md: "100%" },
                                                    borderRadius: "5px",
                                                },
                                            }}
                                        />
                                        {touched.dateOfExpiry && errors.dateOfExpiry && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.dateOfExpiry}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Status</Typography>
                                        <Select
                                            name="status"
                                            options={statusOptions}
                                            value={statusOptions.find(opt => opt.value === String(values.status)) || null}
                                            onChange={(option) => setFieldValue("status", option.value)}
                                            onBlur={() => setFieldTouched("status", true)}
                                            placeholder="Select Status"
                                        />
                                        {touched.status && errors.status && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.status}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={{ ...rightLabelStyle, mb: 1, mt: 1 }}>
                                            Date Of Release
                                        </Typography>
                                        <TextField
                                            type="date"
                                            name="dateOfRelease"
                                            value={values.dateOfRelease}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            onClick={(e) => {
                                                const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                if (input) input.showPicker?.();
                                            }}
                                            fullWidth
                                            sx={{
                                                ...rightTextStyle,
                                                "& input::-webkit-calendar-picker-indicator": {
                                                    display: "none",
                                                    WebkitAppearance: "none",
                                                },
                                                "& input[type='date']": {
                                                    appearance: "none",
                                                    WebkitAppearance: "none",
                                                },
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": {
                                                        border: "none",
                                                    },
                                                },
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}>
                                                        {CalenderSVG}
                                                    </Box>
                                                ),
                                                sx: {
                                                    backgroundColor: "#fff",
                                                    border: '1px solid',
                                                    borderColor: 'hsl(0, 0%, 80%)',
                                                    height: "40px",
                                                    width: { xs: "100%", md: "100%" },
                                                    borderRadius: "5px",
                                                },
                                            }}
                                        />
                                        {touched.dateOfRelease && errors.dateOfRelease && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.dateOfRelease}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Days To Expiry</Typography>
                                        <TextField
                                            fullWidth
                                            name="daysToExpiry"
                                            value={values.daysToExpiry}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter Days To Expiry"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.daysToExpiry && errors.daysToExpiry && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.daysToExpiry}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Expiry Alert</Typography>
                                        <TextField
                                            fullWidth
                                            name="expiryAlert"
                                            value={values.expiryAlert}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter Proposed Expiry Alert"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.expiryAlert && errors.expiryAlert && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.expiryAlert}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Validity (Days)</Typography>
                                        <TextField
                                            fullWidth
                                            name="validityDays"
                                            value={values.validityDays}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter Validity (Days)"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.validityDays && errors.validityDays && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.validityDays}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={{ ...rightLabelStyle, mb: 1, mt: 1 }}>
                                            Renewal Date
                                        </Typography>
                                        <TextField
                                            type="date"
                                            name="renewalDate"
                                            value={values.renewalDate}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            onClick={(e) => {
                                                const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                if (input) input.showPicker?.();
                                            }}
                                            fullWidth
                                            sx={{
                                                ...rightTextStyle,
                                                "& input::-webkit-calendar-picker-indicator": {
                                                    display: "none",
                                                    WebkitAppearance: "none",
                                                },
                                                "& input[type='date']": {
                                                    appearance: "none",
                                                    WebkitAppearance: "none",
                                                },
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": {
                                                        border: "none",
                                                    },
                                                },
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}>
                                                        {CalenderSVG}
                                                    </Box>
                                                ),
                                                sx: {
                                                    backgroundColor: "#fff",
                                                    border: '1px solid',
                                                    borderColor: 'hsl(0, 0%, 80%)',
                                                    height: "40px",
                                                    width: { xs: "100%", md: "100%" },
                                                    borderRadius: "5px",
                                                },
                                            }}
                                        />
                                        {touched.renewalDate && errors.renewalDate && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.renewalDate}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Claim Period (Days)</Typography>
                                        <TextField
                                            fullWidth
                                            name="claimPeriodDays"
                                            value={values.claimPeriodDays}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter Claim Period (Days)"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.claimPeriodDays && errors.claimPeriodDays && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.claimPeriodDays}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Remarks / Conditions</Typography>
                                        <TextField
                                            fullWidth
                                            name="remarksOrConditions"
                                            value={values.remarksOrConditions}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter Remarks / Conditions"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.remarksOrConditions && errors.remarksOrConditions && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.remarksOrConditions}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={{ ...rightLabelStyle, mb: 1, mt: 1 }}>
                                            Last Updated
                                        </Typography>
                                        <TextField
                                            type="date"
                                            name="lastUpdated"
                                            value={values.lastUpdated}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            onClick={(e) => {
                                                const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                if (input) input.showPicker?.();
                                            }}
                                            fullWidth
                                            sx={{
                                                ...rightTextStyle,
                                                "& input::-webkit-calendar-picker-indicator": {
                                                    display: "none",
                                                    WebkitAppearance: "none",
                                                },
                                                "& input[type='date']": {
                                                    appearance: "none",
                                                    WebkitAppearance: "none",
                                                },
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": {
                                                        border: "none",
                                                    },
                                                },
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}>
                                                        {CalenderSVG}
                                                    </Box>
                                                ),
                                                sx: {
                                                    backgroundColor: "#fff",
                                                    border: '1px solid',
                                                    borderColor: 'hsl(0, 0%, 80%)',
                                                    height: "40px",
                                                    width: { xs: "100%", md: "100%" },
                                                    borderRadius: "5px",
                                                },
                                            }}
                                        />
                                        {touched.lastUpdated && errors.lastUpdated && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.lastUpdated}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={12}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "flex-end",
                                                alignItems: "center",
                                            }}
                                        >
                                            <Button onClick={handleClose}
                                                sx={{
                                                    textTransform: "none",
                                                    color: "#F15744",
                                                    fontSize: "14px",
                                                    lineHeight: "100%",
                                                    fontFamily: "Outfit, sans-serif",
                                                    borderRadius: "25px",
                                                    fontWeight: 500,
                                                    px: "20px",
                                                    py: "15px",
                                                }}>
                                                Cancel
                                            </Button>
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                // loading={isLoading}
                                                sx={{
                                                    backgroundColor: "#00344C",
                                                    borderRadius: "25px",
                                                    textTransform: "none",
                                                    fontWeight: 500,
                                                    fontSize: "14px",
                                                    lineHeight: "100%",
                                                    fontFamily: "Outfit, sans-serif",
                                                    color: "#FFFFFF",
                                                    px: "20px",
                                                    py: "15px",
                                                    display: "flex",
                                                    gap: 1,
                                                    "&.Mui-disabled": {
                                                        backgroundColor: "#00344C",
                                                        color: "#fff",
                                                        opacity: 0.7,
                                                    },
                                                }}
                                                disabled={isLoading}
                                            >
                                                {isLoading && <CircularProgress size={16} color="inherit" />}
                                                Save
                                            </Button>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </form>
                        )}
                    </Formik>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default EditRBG;